import { CDK_DRAG_CONFIG, CdkDragDrop, DragDropConfig } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  Signal,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  isAfter,
  isBefore,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  observeOn,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  makeStylesForTag,
  RuntimeEventQueryData,
  TagColorPalette,
} from '@blaze-runtime/engine-domain';
import { markEventAsHandled, utcToCalendarWallTime } from '@blaze-runtime/engine-util';
import {
  ComparisonOperator,
  LogicalQueryNode,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { mapSimpleQueryConditionsToComplexFilters } from '@blaze-shared/datasets-domain';
import {
  DataBindingSourceInfo,
  DEFAULT_ELEMENT_PADDING,
  ElementLabelProps,
  FormContextCalendar,
  getActiveBinding,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_DELETE,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW_DIALOG,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  PaginationDirection,
  PropertiesConfigInputContainerItemActionModel,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import { isFormula } from '@blaze-shared/formulas-domain-language-grammar';
import { compareAsc } from '@blaze-shared/platform-util';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetColumn,
  DatasetRow,
  DatasetSchema,
  findColumn,
  getColumnKey,
  getColumnQuery,
  getIdColumnName,
  isDatasetLinkValue,
  matchColumn,
} from '@blaze-shared/schema-domain';
import {
  colIdExactRegex,
  countedShareReplay,
  isDeepEqual,
  resizeRxUtils,
  toInt,
  trackByIndex,
  useEmitTracker,
  ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';
import {
  findFirstNodeInWorkflowOfType,
  GetWorkflowNode,
  isGetNode,
} from '@blaze-shared/workflow-domain';

import { arePropsWithData } from '../../../canvas-elements/element-model/element-props-guards';
import { ElementInputProviderService } from '../../../canvas-elements/io-data/inputs/element-input-data-provider.service';
import {
  CalendarDateAdapter,
  CalendarDatesViewComponent,
  CalendarDay,
} from '../../../components/calendar-dates-view';
import { ContainerActionsService } from '../../../services/container-actions.service';
import { RowGenerationService } from '../../../services/row-generation.service';
import { CanvasElementBaseComponent } from '../../__base/base.component';
import {
  CALENDAR_DEFAULTS,
  CalendarDayViewOptions,
  CalendarPropsModel,
} from '../calendar-element.models';
import { CalendarWeekViewComponent } from '../calendar-week-view/calendar-week-view.component';
import {
  Calendar,
  CalendarConfig,
  CalendarDataSource,
  CalendarDateFormat,
  CalendarEvent,
  CalendarTimeFormat,
  CalendarTimeScale,
  CalendarView,
  EventChangeEvent,
} from '../calendar.models';
import { CalendarsFilterComponent } from '../calendars-filter/calendars-filter.component';
import { CalendarDayViewLayoutService } from '../layout/calendar-day-view-layout.service';
import { DynamicFormatDate } from '../providers/calendar-date-formats';
import {
  CalendarService,
  DAYS_IN_WEEK,
  DAYS_IN_WEEK_WITHOUT_WEEKENDS,
} from '../providers/calendar.service';
import { CALENDAR_CONFIG, calendarConfig } from '../providers/config';
import {
  applyEventChange,
  applyEventDrop,
  DEFAULT_CALENDAR_NAME,
  DEFAULT_CALENDAR_NAME_TOKEN,
  getEmptyCalendar,
  getNewCalendarEvent,
  getShouldShowTimezones,
} from '../utils/calendar-event.utils';
import { getCalendarRangeQuery, getDefaultCalendarRange } from '../utils/calendar-query.utils';

/** If the calendar's viewport is narrower than this value, it'll go into single calendar swiping mode */
const SWIPING_MODE_THRESHOLD_PX = 480;
const REMEMBERED_CALENDAR_PLACEHOLDER = '!!placeholder';

type IntermediateCalendarInfo = {
  schema: BlazeSchema;
  columns: CalendarPropsModel['content']['input'] | undefined;
  idColumn: string;
  calendarTableRows: DatasetRow[];
} & (
  | {
      columnType: 'String' | 'Tag';
    }
  | {
      columnType: 'Link';
      calendarTableSchema: DatasetSchema;
    }
);

type PaletteSetting = {
  palette?: TagColorPalette;
  foreground?: string;
};

@UntilDestroy()
@Component({
  selector: 'be-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CalendarDayViewLayoutService,
    DynamicFormatDate,
    CalendarDateAdapter,
    {
      provide: DateAdapter,
      useExisting: CalendarDateAdapter,
    },
    {
      provide: CALENDAR_CONFIG,
      useValue: calendarConfig,
    },
    {
      provide: MAT_DATE_FORMATS,
      useExisting: DynamicFormatDate,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: CDK_DRAG_CONFIG, useValue: { zIndex: 1201 } as DragDropConfig },
  ],
  standalone: false,
})
export class CalendarComponent
  extends CanvasElementBaseComponent<'calendar'>
  implements AfterViewInit, OnChanges, OnInit
{
  private readonly calendarService = inject(CalendarService);
  private readonly elementInputProviderService = inject(ElementInputProviderService);
  private readonly config = inject<CalendarConfig>(CALENDAR_CONFIG);
  private readonly containerActionsService = inject(ContainerActionsService);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly dateFormatProvider = inject(DynamicFormatDate);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly layoutService = inject(CalendarDayViewLayoutService);
  private readonly rowGenerationService = inject(RowGenerationService);

  @Input() value: DatasetRow[] = [];

  @ViewChild('scrollXContainer', { static: true }) scrollXContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('paginationWrapper') paginationWrapper: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('calendarWorkspace') calendarWorkspace!: ElementRef<HTMLElement>;
  @ViewChild(CalendarDatesViewComponent) calendarMonthViewComponent:
    | CalendarDatesViewComponent
    | undefined;
  @ViewChild(CalendarWeekViewComponent) calendarWeekViewComponent:
    | CalendarWeekViewComponent
    | undefined;
  @ViewChildren(CalendarsFilterComponent) calendarFilterComponent?: QueryList<
    CalendarsFilterComponent | undefined
  >;

  @HostBinding('style.--b-cr-internal-padding') internalPadding? = DEFAULT_ELEMENT_PADDING;
  @HostBinding('style.--b-cr-titles-color') titlesColor = '#000000';
  @HostBinding('style.--b-cr-grid-background-color') gridBackgroundColor = '#ffffff';
  @HostBinding('style.--b-cr-header-background-color') headerBackgroundColor = '#eeeeee';
  @HostBinding('style.--b-cr-lines-color') linesColor = '#888888';
  @HostBinding('style.--b-cr-secondary-lines-color') secondaryLinesColor = '#bbb';
  @HostBinding('style.--b-cr-appointment-outline-color') calendarOutlineColor = '#ffffff';
  @HostBinding('style.--b-cr-date-selector-default-text') dateSelectorDefaultText = '#000';
  @HostBinding('style.--b-cr-date-selector-default-background') dateSelectorDefaultBackground =
    '#000';
  @HostBinding('style.--b-cr-date-selector-default-border-color') dateSelectorDefaultBorderColor =
    'transparent';
  @HostBinding('style.--b-cr-date-selector-default-border-width') dateSelectorDefaultBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-default-border-radius') dateSelectorDefaultBorderRadius =
    '16px';
  @HostBinding('style.--b-cr-date-selector-hover-text') dateSelectorHoverText = '#000';
  @HostBinding('style.--b-cr-date-selector-hover-background') dateSelectorHoverBackground = '#000';
  @HostBinding('style.--b-cr-date-selector-hover-border-color') dateSelectorHoverBorderColor =
    '#00f';
  @HostBinding('style.--b-cr-date-selector-hover-border-width') dateSelectorHoverBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-hover-border-radius') dateSelectorHoverBorderRadius =
    '16px';
  @HostBinding('style.--b-cr-date-selector-selected-text') dateSelectorSelectedText = '#fff';
  @HostBinding('style.--b-cr-date-selector-selected-background') dateSelectorSelectedBackground =
    '#00f';
  @HostBinding('style.--b-cr-date-selector-selected-border-color') dateSelectorSelectedBorderColor =
    'transparent';
  @HostBinding('style.--b-cr-date-selector-selected-border-width') dateSelectorSelectedBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-selected-border-radius')
  dateSelectorSelectedBorderRadius = '16px';
  @HostBinding('style.--b-cr-date-selector-day-with-events') dateSelectorDayWithEvents = '#333';

  private readonly allCalendars$$ = new BehaviorSubject<Calendar[]>([]);
  private readonly queryEmitTracker = useEmitTracker();
  private readonly runtimeViewMode$$ = new BehaviorSubject<CalendarView | undefined>(undefined);
  private readonly selectedIds$$ = new BehaviorSubject<string[]>([]);
  private readonly scrollData$$ = new BehaviorSubject<{ left: number; width: number } | undefined>(
    undefined,
  );

  private areAllSelected = true;
  private calendarInfo: IntermediateCalendarInfo | null = null;
  private currentCalendars: Calendar[] = [];
  private queryRange = getDefaultCalendarRange();
  private selectedDate = new Date();

  private readonly TIME_SCALE_MAPPING: Record<CalendarTimeScale, number> = {
    [CalendarTimeScale['1H']]: 1,
    [CalendarTimeScale['30MIN']]: 0.5,
  };

  componentStyles: Record<string, string> = {};

  readonly dataValue = this.getDataInput('value');
  readonly CalendarView = CalendarView;

  readonly trackByIndex = trackByIndex;
  readonly dayViewHeaderHeightPx = 50;
  readonly weekViewHeaderHeightPx = 77;
  readonly calendarFilterRowHeightPx = 45;
  timeLineWidthPx = 54;

  readonly currentCalendarPageIndex$: Observable<number>;
  timeScale = 1;
  workingHours: { min: number; max: number } = { min: 0, max: 24 };
  timeline: string[] = [];
  wrapperStyles: Record<string, string> | null = null;
  calendarsStyles: Record<string, Record<string, string>> = {};
  currentCalendarsPalette?: PaletteSetting;

  weekViewDateRange: Date[] = [];

  allCalendars$ = this.allCalendars$$.pipe(distinctUntilChanged(isDeepEqual));
  calendars$ = combineLatest({
    calendars: this.allCalendars$$,
    ids: this.selectedIds$$,
  }).pipe(
    distinctUntilChanged(isDeepEqual),
    map(({ calendars, ids }) => {
      return calendars.filter(
        (c) => (c.id && ids.includes(c.id)) || (c.name && ids.includes(c.name)),
      );
    }),
  );
  calendarsCount$ = this.calendars$.pipe(map((calendars) => calendars?.length ?? 0));

  datesWithEvents: Date[] = [];

  calendarViewMode = signal<CalendarView>(CalendarView.Day);
  calendarViewMode$ = toObservable(this.calendarViewMode);
  isCalendarVisible = true;
  isFilterVisible = false;
  showTimezones = false;
  isEventDragDisabled = false;

  /**
   * If true, the calendars use full width of the calendar workspace and the horizontal
   * scroll is snapping, so the calendars can be swiped easily
   */
  isSwipingMode = signal(false);
  dayViewColumnWidth = signal<ValueWithUnitOfMeasure | undefined>(
    `${this.config.defaultDayViewColumnWidthPx}px`,
  );
  eventDropTimeDelta!: { h: number; m: number };

  isAddEnabled: Signal<boolean | undefined>;
  isEditEnabled: Signal<boolean | undefined>;
  isDeleteEnabled: Signal<boolean | undefined>;
  isDetailsViewEnabled: Signal<boolean | undefined>;

  readonly markEventAsHandled = markEventAsHandled;

  set selectedCalendarDate(date: Date) {
    this.selectedDate = date;

    this.createWeekViewDateRange();

    if (this.calendarViewMode() === CalendarView.Week) {
      this.focusWeekViewColumn(this.weekViewDateRange.findIndex((d) => isSameDay(d, date)));
    }
  }
  get selectedCalendarDate(): Date {
    return this.selectedDate;
  }

  set runtimeViewMode(value: CalendarView | undefined) {
    this.runtimeViewMode$$.next(value);
  }
  get runtimeViewMode(): CalendarView | undefined {
    return this.runtimeViewMode$$.value;
  }

  get scrollSnapDirection(): 'x' | 'none' {
    return this.isSwipingMode() ? 'x' : 'none';
  }

  get headerHeightPx(): number {
    return this.calendarViewMode() === CalendarView.Week ||
      this.calendarViewMode() === CalendarView.ThreeDay
      ? this.weekViewHeaderHeightPx
      : this.dayViewHeaderHeightPx;
  }

  get allCalendarsLength(): number {
    return this.allCalendars$$.getValue().length;
  }

  get calendarTitle(): ElementLabelProps {
    return this.props?.content.title ?? CALENDAR_DEFAULTS.content.title;
  }

  get calendarsLabel(): string {
    return this.props?.content.calendarsLabel ?? CALENDAR_DEFAULTS.content.calendarsLabel;
  }

  get viewModeLabel(): string {
    switch (this.calendarViewMode()) {
      case CalendarView.Day:
        return 'Day';
      case CalendarView.ThreeDay:
        return '3 Day';
      case CalendarView.Week:
        return 'Week';
      case CalendarView.Month:
        return 'Month';
      default:
        return '';
    }
  }

  get dateFormat(): CalendarDateFormat {
    return (
      this.props?.content.calendarOptions?.dateFormat ??
      CALENDAR_DEFAULTS.content.calendarOptions.dateFormat
    );
  }

  get timeFormat(): CalendarTimeFormat {
    return (
      this.props?.content.calendarOptions?.timeFormat ??
      CALENDAR_DEFAULTS.content.calendarOptions.timeFormat
    );
  }

  get firstDayOfWeek(): CalendarDay {
    return (
      this.props?.content.calendarOptions?.startWeekOn ??
      CALENDAR_DEFAULTS.content.calendarOptions.startWeekOn
    );
  }

  get hideWeekends(): boolean {
    return (
      this.props?.content.calendarOptions?.hideWeekends ??
      CALENDAR_DEFAULTS.content.calendarOptions.hideWeekends
    );
  }

  get defaultDurationMinutes(): number {
    return (
      this.props?.content.calendarOptions?.defaultDurationMinutes ??
      CALENDAR_DEFAULTS.content.calendarOptions.defaultDurationMinutes
    );
  }

  get showLabels(): boolean {
    return (
      this.allCalendars$$.getValue().length > 1 ||
      this.calendarViewMode() === CalendarView.ThreeDay ||
      this.calendarViewMode() === CalendarView.Week ||
      !this.props?.content.calendarOptions?.hideCalendarName
    );
  }

  get eventFontSize(): ValueWithUnitOfMeasure {
    return this.props?.style?.appointments?.fontSize ?? '13px';
  }

  get oneHourHeight(): number {
    return this.config.oneHourHeight;
  }

  constructor() {
    super();

    this.currentCalendarPageIndex$ = combineLatest({
      scroll: this.scrollData$$,
      calendars: this.calendars$,
    }).pipe(
      // Wait for the animation frame to avoid UI jitter when scrolling
      observeOn(animationFrameScheduler),
      distinctUntilChanged(isDeepEqual),
      map(({ scroll, calendars }) => {
        return scroll ? Math.round((scroll.left * calendars.length) / scroll.width) : 0;
      }),
      startWith(0),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    combineLatest({
      runtimeViewMode: this.runtimeViewMode$$,
      propsView: this.props$.pipe(map((props) => props?.content?.calendarOptions?.view)),
    })
      .pipe(
        untilDestroyed(this),
        map(
          ({ runtimeViewMode, propsView }) =>
            runtimeViewMode ?? propsView ?? CALENDAR_DEFAULTS.content.calendarOptions.view,
        ),
        distinctUntilChanged(),
      )
      .subscribe((calendarViewMode) => {
        this.calendarViewMode.set(calendarViewMode);
      });

    this.createPlaceholderCalendar();

    this.isAddEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemAdd.enabled,
        expression: props.content.itemAdd.visibilityExpression,
      })),
    );
    this.isEditEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemEdit.enabled,
        expression: props.content.itemEdit.visibilityExpression,
      })),
    );
    this.isDeleteEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemDelete.enabled,
        expression: props.content.itemDelete.visibilityExpression,
      })),
    );
    this.isDetailsViewEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemViewDialog?.enabled,
        expression: props.content.itemView?.visibilityExpression,
      })),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const propsChange = changes['props'];
    if (!propsChange) {
      return;
    }

    const timeScale = this.props?.content.calendarOptions?.timeScale ?? CalendarTimeScale['1H'];
    this.timeScale = this.TIME_SCALE_MAPPING[timeScale];
    this.timeLineWidthPx = this.timeScale === 1 ? 54 : 78;
    this.workingHours = this.getWorkingHours();
    this.timeline = this.layoutService.getTimeline(
      this.timeFormat,
      this.timeScale,
      this.workingHours,
    );
    this.isEventDragDisabled = this.props?.content.calendarOptions.disableEventDrag ?? false;

    this.updateStyles();

    const previousCalendarOptions = propsChange.previousValue?.content?.calendarOptions;
    const currentCalendarOptions = propsChange.currentValue?.content?.calendarOptions;

    if (previousCalendarOptions?.dateFormat !== currentCalendarOptions?.dateFormat) {
      this.dateFormatProvider.setDisplayFormat(this.dateFormat, 'monthYearLabel');
      this.calendarMonthViewComponent?.focusCell(this.selectedDate);
    }
  }

  ngOnInit(): void {
    this.updateDefaultEventSchema();
    this.isPending$
      .pipe(
        first((pending) => !pending),
        switchMap(() => this.getCalendarsWithData()),
        untilDestroyed(this),
      )
      .subscribe(({ calendars, data, hasEventsWithoutCalendar }) => {
        this.updateCalendarsCount(calendars.length);
        const input = this.props?.content.input;
        if (!input) {
          return;
        }

        const dataSource = this.mapDatasetRowToCalendarDataSource(data || [], input);
        this.updateDatesWithEvents(dataSource);

        const allCalendars = this.calendarService.createCalendars(
          dataSource,
          calendars,
          !!hasEventsWithoutCalendar,
        );
        this.showTimezones = getShouldShowTimezones(allCalendars);

        const currentSelection = this.selectedIds$$.value;
        let selectedCalendarsIds = calendars.map(
          (c) => c.id || DEFAULT_CALENDAR_NAME_TOKEN,
        ) as string[];
        if (!this.areAllSelected && currentSelection.length) {
          const currentCalendars = this.currentCalendars.filter((cc) =>
            currentSelection.includes(cc.id || DEFAULT_CALENDAR_NAME_TOKEN),
          );
          const selectedCalendars = currentCalendars
            .map((cc) => {
              const match = calendars.find(
                (c) =>
                  c.id === cc.id ||
                  (!c.id && cc.id == DEFAULT_CALENDAR_NAME_TOKEN) ||
                  c.name === cc.name,
              );
              return match ? match.id || DEFAULT_CALENDAR_NAME_TOKEN : null;
            })
            .filter(Boolean);
          const matches = selectedCalendarsIds.filter((id) => selectedCalendars.includes(id));
          if (matches.length) {
            selectedCalendarsIds = matches;
          }
        }
        if (!isDeepEqual(this.selectedIds$$.value, selectedCalendarsIds)) {
          this.selectedIds$$.next(selectedCalendarsIds);

          if (this.calendarFilterComponent?.length) {
            for (const c of this.calendarFilterComponent) {
              c?.updateSelectedCalendarsIds(selectedCalendarsIds);
            }
          }
        }

        this.allCalendars$$.next(allCalendars);

        this.currentCalendars = calendars;
        this.createWeekViewDateRange();
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.initDayViewColumnWidthUpdate();

    this.viewportMode$.pipe(untilDestroyed(this)).subscribe((viewPort) => {
      if (viewPort === 'mobile') {
        return;
      }

      this.isCalendarVisible = true;
      if (this.runtimeViewMode === CalendarView.ThreeDay) {
        this.onSwitchToWeekView();
      }
      this.cdr.markForCheck();
    });
  }

  calendarsSelectionChange(e: { ids: string[]; all: boolean }): void {
    this.selectedIds$$.next(e.ids);
    this.areAllSelected = e.all;
  }

  toggleFilters(): void {
    this.isFilterVisible = !this.isFilterVisible;
  }

  onMonthViewDaySelection(date: Date): void {
    this.selectedCalendarDate = date;
    this.updateQueryOutput();
  }

  onIsCalendarVisibleChange(visible: boolean): void {
    this.isCalendarVisible = visible;
    this.updateQueryOutput();
  }

  selectPreviousDay(): void {
    this.selectDay(-1);
  }

  selectPreviousPeriod(): void {
    this.selectPeriod(-1);
  }

  selectNextDay(): void {
    this.selectDay(1);
  }

  selectNextPeriod(): void {
    this.selectPeriod(1);
  }

  onTodayClick(): void {
    this.selectDate(new Date());
  }

  getEventsForSelectedDate(calendar: Calendar): CalendarEvent[] {
    const cd = this.selectedCalendarDate;
    return calendar.events[cd.getFullYear()]?.[cd.getMonth()]?.[cd.getDate()] ?? [];
  }

  onAddClick(calendarEvent?: Partial<CalendarEvent>, calendar?: Calendar): void {
    const dataRow: DatasetRow = {};
    const columns = this.props?.content.input;

    const startField = this.getColumnName(columns?.startColumn);
    const endField = this.getColumnName(columns?.endColumn);
    const calendarField = this.getColumnName(columns?.calendarColumn);
    const timezoneField = this.getColumnName(columns?.timeZoneColumn);

    // Prepopulate the start and end date time if provided
    calendarEvent ??= getNewCalendarEvent(this.selectedCalendarDate, this.defaultDurationMinutes);
    if (startField) {
      dataRow[startField] = calendarEvent.startDate?.toISOString();
    }
    if (endField) {
      dataRow[endField] = calendarEvent.endDate?.toISOString();
    }

    // Prepopulate calendar and timezone
    if (calendarField) {
      dataRow[calendarField] = calendar?.id ?? calendar?.name;
    }
    if (timezoneField) {
      dataRow[timezoneField] = calendar?.timeZone;
    }

    this.onAction({ id: ITEMS_CONTAINER_ACTION_ADD, label: 'New Event' }, dataRow);
  }

  onDetailsClick(calendarEvent: CalendarEvent): void {
    const dataRow = this.getDataRowById(calendarEvent.id);
    if (!dataRow) {
      console.warn(`[Calendar] Missing data row with id ${calendarEvent.id}`);
      return;
    }

    let label = calendarEvent.title;
    if (label.length > 25) {
      label = label.slice(0, 25) + '...';
    }

    this.onAction({ id: ITEMS_CONTAINER_ACTION_VIEW_DIALOG, label }, dataRow);
  }

  onEditClick(calendarEvent: CalendarEvent): void {
    const dataRow = this.getDataRowById(calendarEvent.id);
    if (!dataRow) {
      console.warn(`[Calendar] Missing data row with id ${calendarEvent.id}`);
      return;
    }

    this.onAction({ id: ITEMS_CONTAINER_ACTION_EDIT, label: 'Edit appointment' }, dataRow);
  }

  onDeleteClick(calendarEvent: CalendarEvent): void {
    const dataRow = this.getDataRowById(calendarEvent.id);
    if (!dataRow) {
      console.warn(`[Calendar] Missing data row with id ${calendarEvent.id}`);
      return;
    }

    this.onAction({ id: ITEMS_CONTAINER_ACTION_DELETE, label: 'Delete appointment' }, dataRow);
  }

  onEventChange(event: EventChangeEvent): void {
    if (event.changeType === 'both') {
      this.eventDropTimeDelta = event.delta;
      return;
    }
    const { startColumn, endColumn } = this.props?.content.input || {};
    const dataRow = this.getDataRowById(event.event.id);
    this.updateEvent(
      applyEventChange(event, dataRow, {
        startField: this.schemaService.getColumnName(startColumn),
        endField: this.schemaService.getColumnName(endColumn),
      }),
    );
  }

  onEventDrop(event: CdkDragDrop<any, any, CalendarEvent>): void {
    const { startColumn, endColumn, calendarColumn } = this.props?.content.input || {};
    const dataRow = this.getDataRowById(event.item.data.id);
    const dropResult = applyEventDrop(event, dataRow, this.eventDropTimeDelta, {
      startField: this.schemaService.getColumnName(startColumn),
      endField: this.schemaService.getColumnName(endColumn),
      calendarField: this.schemaService.getColumnName(calendarColumn),
      calendarFieldType: this.calendarInfo?.columnType,
    });
    const eventEl = event.item.element.nativeElement;
    eventEl.style.top = `${eventEl.getAttribute('topPx')}px`;
    eventEl.style.left = `${eventEl.getAttribute('leftPx')}%`;
    eventEl.style.zIndex = `${eventEl.getAttribute('zi')}`;
    if (!dropResult) {
      return;
    }

    const { updated, sameDayViewChange } = dropResult;

    if (!sameDayViewChange) {
      eventEl.style.opacity = '0.5';
    }
    this.updateEvent(updated);
  }

  trackByIdOrIndex(index: number, calendar: Calendar): string | number {
    return calendar.id ?? index;
  }

  updateCalendarScroll(event: Event): void {
    const viewport = event.target as HTMLElement;
    this.scrollData$$.next({
      left: viewport.scrollLeft,
      width: viewport.scrollWidth,
    });
  }

  getFiltersContainerHeight(calendarsCount: number): string {
    if (!this.isSwipingMode()) {
      return 'auto';
    }

    return `${Math.min(calendarsCount + 1, 6) * this.calendarFilterRowHeightPx}px`;
  }

  onSwitchToDayView(selectedDate?: Date): void {
    if (selectedDate) {
      this.selectDate(selectedDate);
    }
    this.runtimeViewMode = CalendarView.Day;
    this.updateQueryOutput();
  }

  onSwitchTo3DayView(): void {
    this.runtimeViewMode = CalendarView.ThreeDay;
    this.updateQueryOutput();
    this.createWeekViewDateRange();
  }

  onSwitchToWeekView(): void {
    this.runtimeViewMode = CalendarView.Week;
    this.updateQueryOutput();
    this.createWeekViewDateRange();
  }

  onSwitchToMonthView(): void {
    this.runtimeViewMode = CalendarView.Month;
    this.updateQueryOutput();
  }

  private onAction(
    action: PropertiesConfigInputContainerItemActionModel,
    dataRow: DatasetRow,
  ): void {
    const actionData = this.getActionData();
    const actionSchema = this.getEventSchema('action');

    if (!actionData || !actionSchema) {
      throw new Error('Missing action data or schema');
    }
    const { dataSource, dataRowIdField, formContext } = actionData;

    this.containerActionsService.handleListlikeAction({
      dataSource,
      action,
      props: this.props as CalendarPropsModel,
      output: this.output,
      dataRow,
      dataRowSchema: actionSchema,
      dataRowId: dataRow[dataRowIdField],
      formContext,
    });
  }

  private focusWeekViewColumn(index: number): void {
    const containerWidth = this.calendarWorkspace.nativeElement.scrollWidth;
    const oneColumnWidth = containerWidth / DAYS_IN_WEEK;
    const offset = Math.floor(DAYS_IN_WEEK_WITHOUT_WEEKENDS / 2);
    const scrollLeft = Math.min(
      oneColumnWidth * (DAYS_IN_WEEK - DAYS_IN_WEEK_WITHOUT_WEEKENDS),
      Math.max(oneColumnWidth * (index - offset), 0),
    );
    this.calendarWorkspace.nativeElement.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }

  private getDatasetRowFieldKeys(columns: CalendarPropsModel['content']['input']): {
    id: string;
    title?: string;
    start?: string;
    end?: string;
    calendar?: string;
    timeZone?: string;
  } {
    return {
      id: this.getDataRowIdField(),
      title: this.getColumnName(columns.titleColumn),
      start: this.getColumnName(columns.startColumn),
      end: this.getColumnName(columns.endColumn),
      calendar: this.getColumnName(columns.calendarColumn),
      timeZone: this.getColumnName(columns.timeZoneColumn),
    };
  }

  private mapDatasetRowToCalendarDataSource(
    value: DatasetRow[],
    columns: CalendarPropsModel['content']['input'],
  ): CalendarDataSource[] {
    if (!value?.length) {
      return [];
    }

    const { id, title, start, end, calendar, timeZone } = this.getDatasetRowFieldKeys(columns);

    if (!title || !start || !end) {
      return [];
    }

    return value
      .filter((row) => row[start] && row[end])
      .map<CalendarDataSource>((row) => {
        const calendarValue = calendar ? row[calendar] : '';

        const calendarName = isDatasetLinkValue(calendarValue)
          ? calendarValue.display
          : (calendarValue ?? DEFAULT_CALENDAR_NAME);

        const calendarId = isDatasetLinkValue(calendarValue)
          ? calendarValue.rowId
          : DEFAULT_CALENDAR_NAME_TOKEN;

        const calendarTimeZone = timeZone ? ((row[timeZone] as string) ?? null) : null;
        const startDate = new Date(row[start]);
        const endDate = new Date(row[end]);

        return {
          id: row[id],
          start: utcToCalendarWallTime(startDate, calendarTimeZone),
          end: utcToCalendarWallTime(endDate, calendarTimeZone),
          calendarId,
          calendarName,
          styles: this.calendarsStyles[calendarName || DEFAULT_CALENDAR_NAME_TOKEN],
          title: row[title],
          calendarTimeZone,
        };
      });
  }

  private createPlaceholderCalendar(): void {
    const calendar: Calendar = {
      id: null,
      name: null,
      timeZone: null,
      events: {},
    };
    this.allCalendars$$.next([calendar]);
    this.selectedIds$$.next([]);
  }

  private getCalendarsWithData(): Observable<{
    calendars: Calendar[];
    data?: DatasetRow[];
    hasEventsWithoutCalendar?: boolean;
  }> {
    return combineLatest({
      props: this.props$,
      dataInput: this.dataValue.dataInput$.pipe(
        map((dataInput) => ({ data: dataInput?.data, schema: dataInput?.schema })),
        filter(
          (dataInput): dataInput is { data: DatasetRow[]; schema: BlazeSchema } =>
            !!dataInput.schema,
        ),
      ),
      context: this.runtimeContext$,
    }).pipe(
      switchMap(({ props, dataInput: { data, schema }, context }) => {
        if (!schema || !props?.content.input?.calendarColumn) {
          return of({ calendarInfo: null, data });
        }

        const calendarColumn: DatasetColumn | undefined = findColumn(
          schema.columns,
          props?.content.input?.calendarColumn,
        );
        let calendarInfo: IntermediateCalendarInfo | null = null;

        if (calendarColumn?.link?.tableId) {
          const calendarColumnLinkTableId = calendarColumn.link.tableId;

          return this.datasetRowsService
            .observeDatasetAndRelatedChanged(calendarColumnLinkTableId)
            .pipe(
              startWith(undefined),
              switchMap(() =>
                this.schemaService.obtainSchema<DatasetSchema>(calendarColumnLinkTableId).pipe(
                  filter(Boolean),
                  switchMap((calendarTableSchema) => {
                    return this.getCalendars(context, calendarTableSchema).pipe(
                      map((calendarTableRows) => ({
                        calendarTableSchema,
                        calendarTableRows,
                      })),
                    );
                  }),
                  map(({ calendarTableSchema, calendarTableRows }) => {
                    const idColumn = getIdColumnName(calendarTableSchema);

                    if (!calendarTableSchema || !idColumn) {
                      return { calendarInfo: null, data };
                    }

                    const calendarInfo: IntermediateCalendarInfo = {
                      schema,
                      calendarTableSchema,
                      columns: props.content.input,
                      idColumn,
                      calendarTableRows,
                      columnType: 'Link',
                    };
                    return { calendarInfo, data };
                  }),
                ),
              ),
            );
        } else if (calendarColumn?.columnType === 'Tag') {
          calendarInfo = {
            schema,
            columns: props.content.input,
            idColumn: 'id',
            calendarTableRows: calendarColumn.tagDefinition?.items as DatasetRow[],
            columnType: 'Tag',
          };
        } else if (
          calendarColumn?.columnType === 'String' ||
          calendarColumn?.columnType === 'Formula'
        ) {
          const dataSource = getActiveBinding(this.props?.data, 'value')
            ?.dataSource as DataBindingSourceInfo;
          return this.elementInputProviderService
            .getColumnFilterDataStream({
              sourceType: 'column-filter',
              id: dataSource.id,
              name: calendarColumn.id,
              columns: [],
            })
            .pipe(
              take(1),
              map((result) => {
                const calendarInfo: IntermediateCalendarInfo = {
                  schema,
                  columns: props.content.input,
                  idColumn: 'value',
                  calendarTableRows: result.data,
                  columnType: 'String',
                };
                return { calendarInfo, data };
              }),
            );
        }
        return of({ calendarInfo, data });
      }),
      map(({ calendarInfo, data }) => {
        this.calendarInfo = calendarInfo;
        if (!calendarInfo) {
          this.updateCalendarStyles([DEFAULT_CALENDAR_NAME_TOKEN], false);
          return { calendars: [getEmptyCalendar()], data };
        }
        const { calendarColumn } = calendarInfo.columns ?? {};
        const calendarColumnName = this.schemaService.getColumnName(calendarColumn);
        if (!calendarColumn || !calendarColumnName) {
          this.updateCalendarStyles([DEFAULT_CALENDAR_NAME_TOKEN], false);
          return { calendars: [] };
        }

        const isLinkColumn = calendarInfo.columnType === 'Link';
        const calendarField = isLinkColumn
          ? calendarInfo.calendarTableSchema?.displayColumn ||
            findColumn(calendarInfo.schema.columns, calendarColumn)?.link?.displayColumn
          : 'value';
        const idColumn = isLinkColumn
          ? getIdColumnName(calendarInfo.calendarTableSchema)
          : calendarInfo.idColumn;

        if (!idColumn || !calendarField) {
          this.updateCalendarStyles([DEFAULT_CALENDAR_NAME_TOKEN], false);
          return { calendars: [] };
        }

        const timeZoneColumnName = isLinkColumn
          ? calendarInfo.calendarTableSchema?.columns.find(
              (column) => column.format?.formatType === 'TimeZone',
            )?.name
          : undefined;
        const isTimeZoneColumnSelected = !!this.props?.content.input?.timeZoneColumn;

        const calendars: Calendar[] = calendarInfo.calendarTableRows.map(
          (row) =>
            ({
              id: row[idColumn],
              name: row[calendarField],
              timeZone:
                timeZoneColumnName && isTimeZoneColumnSelected
                  ? row[timeZoneColumnName]
                  : Intl.DateTimeFormat().resolvedOptions().timeZone,
              events: {},
            }) as Calendar,
        );

        const hasEventsWithoutCalendar = data.some((row) => !row[calendarColumnName]);
        if (hasEventsWithoutCalendar) {
          calendars.unshift({
            id: null,
            name: DEFAULT_CALENDAR_NAME,
            timeZone: null,
            events: {},
          });
        }

        this.updateCalendarStyles(
          calendars.map((c) => c.name || DEFAULT_CALENDAR_NAME_TOKEN).sort(compareAsc),
          hasEventsWithoutCalendar,
        );

        return {
          calendars: calendars.sort(this.calendarService.sort),
          data,
          hasEventsWithoutCalendar,
        };
      }),
    );
  }

  private updateCalendarStyles(calendarNames: string[], hasEventsWithoutCalendar: boolean): void {
    // Merge existing calendar names with the default calendar name token if there are events without a calendar
    const newCalendarNames = [
      ...calendarNames,
      ...(hasEventsWithoutCalendar && !calendarNames.some((name) => !name)
        ? [DEFAULT_CALENDAR_NAME_TOKEN]
        : []),
    ].filter((calName) => !(calName in this.calendarsStyles));

    // Create an array of placeholders for existing calendar styles to keep their colors
    const existingCalendarPlaceholders = [
      ...new Array(Object.keys(this.calendarsStyles).length).fill(REMEMBERED_CALENDAR_PLACEHOLDER),
    ].map((val, i) => val + i);

    const newCalendarsPalette: PaletteSetting = {
      palette: this.elementTagsService.mapValuesToColorPalette(
        [],
        undefined,
        undefined,
        this.props?.style.appointments?.palette,
      ),
      foreground: this.props?.style.appointments?.palette.foreground,
    };
    const paletteRetained =
      this.currentCalendarsPalette === undefined ||
      isDeepEqual(this.currentCalendarsPalette, newCalendarsPalette);
    this.currentCalendarsPalette = newCalendarsPalette;

    // Combine placeholders with the new calendar names that need to be assigned styles
    const allCalendarNamesToStyle = paletteRetained
      ? [...existingCalendarPlaceholders, ...newCalendarNames]
      : Object.keys(this.calendarsStyles);

    // Generate a color palette for all calendar names (both placeholders and new names)
    const colorPalette = this.elementTagsService.mapValuesToColorPalette(
      allCalendarNamesToStyle,
      undefined,
      undefined,
      this.props?.style.appointments?.palette,
      this.props?.style.appointments?.palette.foreground,
      true,
    );

    // reassign new values but omit placeholders
    this.calendarsStyles = Object.assign(
      this.calendarsStyles,
      Object.fromEntries(
        allCalendarNamesToStyle
          .filter((val) => !val.startsWith(REMEMBERED_CALENDAR_PLACEHOLDER))
          .map((calendarName) => [calendarName, makeStylesForTag(calendarName, colorPalette)]),
      ),
    );
  }

  private getCalendars(
    context: RuntimeContext,
    calendarTableSchema: DatasetSchema,
  ): Observable<DatasetRow[]> {
    const calendarColumnIdOrName = this.props?.content.input?.calendarColumn;
    const valueBinding = getActiveBinding(this.props?.data, 'value');
    if (
      !calendarColumnIdOrName ||
      !valueBinding ||
      valueBinding.dataSource?.sourceType !== 'context'
    ) {
      return of([]);
    }

    return this.store.select(runtimeSelectors.selectWorkflowById(valueBinding.dataSource.id)).pipe(
      switchMap((inputWorkflow) => {
        let getNode = findFirstNodeInWorkflowOfType(inputWorkflow, isGetNode);
        const datasetIdFromColumnId = colIdExactRegex.exec(calendarColumnIdOrName);
        let datasetId = datasetIdFromColumnId?.[1];
        if (getNode?.datasetId === datasetId) {
          // consistency
        } else if (datasetId) {
          getNode = undefined;
        } else if (getNode) {
          datasetId = getNode.datasetId;
        }
        if (!datasetId) {
          return of([]);
        }
        return this.schemaService.obtainSchema<DatasetSchema>(datasetId).pipe(
          switchMap((getNodeSchema) => {
            const calendarColumn = findColumn(getNodeSchema?.columns, calendarColumnIdOrName);
            if (!calendarColumn) {
              return of([]);
            }

            const calendarQueryConditions =
              getNode &&
              this.buildQueryConditionsFromGetNode(getNode, calendarTableSchema, context);

            if (calendarQueryConditions?.children.length) {
              return this.datasetRowsService.get({
                datasetId: calendarTableSchema?.id,
                filterQuery: calendarQueryConditions,
                includeRowId: false,
              });
            } else {
              return this.datasetRowsService.get({ datasetId: calendarTableSchema.id }).pipe(
                map((rows) => {
                  // // There is a calendar query condition applied
                  const newRow = this.rowGenerationService.getNewRow(valueBinding.dataSource);
                  const idColumn = getIdColumnName(calendarTableSchema);
                  const calendarColumnId = newRow[calendarColumn.name];

                  return rows.filter((row) =>
                    calendarColumnId && idColumn ? calendarColumnId === row[idColumn] : true,
                  );
                }),
              );
            }
          }),
        );
      }),
    );
  }

  private buildQueryConditionsFromQueryNode(
    query: LogicalQueryNode,
    calendarTableSchema: DatasetSchema,
    context: RuntimeContext,
  ): LogicalQueryNode {
    const root: LogicalQueryNode = {
      nodeType: QueryNodeType.Logical,
      logicalType: query.logicalType,
      children: [],
    };

    for (const child of query.children) {
      if (child.nodeType === QueryNodeType.Logical) {
        const logicalNode = this.buildQueryConditionsFromQueryNode(
          child,
          calendarTableSchema,
          context,
        );
        if (logicalNode.children.length > 0) {
          root.children.push(logicalNode);
        }

        continue;
      }

      for (const column of calendarTableSchema.columns ?? []) {
        const matchingColumns = column.dependentColumnInfos?.filter((dci) => {
          return matchColumn(
            getColumnQuery(child.column),
            getColumnQuery({
              id: dci.columnId,
              name: dci.columnName,
            }),
          );
        });

        if (matchingColumns?.length) {
          const value = this.evaluateExpression(child.value, context, false, false);

          // We cannot always copy isFormula value from the child because after evaluation it may
          // stop being a formula value.
          // For example, if a child has a value $$Account["Name"], that value can be treated as a
          // formula and will work as such, however, after evaluating it to "Blaze", query with a
          // simple string and isFormula=true will throw an error.
          const isFormulaValue = isFormula(value);

          if (value) {
            root.children.push({
              column: getColumnKey(column),
              displayInfos: [],
              isFormulaValue,
              isOptional: child.isOptional,
              nodeType: QueryNodeType.Comparison,
              operator: ComparisonOperator.Eq,
              value,
            });
          }
        }
      }
    }

    return root;
  }

  private buildQueryConditionsFromGetNode(
    getNode: GetWorkflowNode,
    calendarTableSchema: DatasetSchema,
    context: RuntimeContext,
  ): LogicalQueryNode | null {
    if (getNode.query) {
      return this.buildQueryConditionsFromQueryNode(getNode.query, calendarTableSchema, context);
    }

    if (getNode.queryConditions) {
      const query = mapSimpleQueryConditionsToComplexFilters(getNode.queryConditions);
      return this.buildQueryConditionsFromQueryNode(query, calendarTableSchema, context);
    }

    return null;
  }

  private selectDate(date: Date): void {
    this.selectedCalendarDate = date;
    this.updateQueryOutput();

    if (this.calendarMonthViewComponent) {
      this.calendarMonthViewComponent.focusCell(this.selectedCalendarDate);
    }
  }

  private getDataRowById(id: string): DatasetRow | undefined {
    const field = this.getDataRowIdField();
    return this.value.find((datasetRow) => datasetRow[field] === id);
  }

  private getDataRowIdField(): string {
    const dataSource = getActiveBinding(this.props?.data, 'value')?.dataSource;
    if (!dataSource) {
      throw new Error('[Calendar] DataSource missing');
    }

    return this.schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType);
  }

  private getWorkingHours(): { min: number; max: number } {
    const time = this.props?.content.time;
    let min = 0;
    let max = 24;

    if (time?.min || time?.from) {
      const from = time.from || time.min || '';
      const { hour, minute } = this.calendarService.parseTimeStringToObject(from);
      min = hour + minute / 60;
      if (this.timeScale === 1) {
        min = Math.floor(min);
      }

      if (time?.max || time?.to) {
        const to = time.to || time.max || '';
        const { hour, minute } = this.calendarService.parseTimeStringToObject(to);
        max = hour + minute / 60;
        if (this.timeScale === 1) {
          max = Math.ceil(max);
        }
      }
    }

    return { min, max };
  }

  private updateCalendarsCount(count: number): void {
    this.store.dispatch(
      runtimeActions.updateElementMeta({
        elementId: this.elementId,
        meta: {
          dataFeatures: {
            elementType: 'calendar',
            calendarsCount: count,
          },
        },
      }),
    );
  }

  private updateDatesWithEvents(dataSource: CalendarDataSource[]): void {
    const datesWithEvents: Date[] = [];

    for (const ds of dataSource) {
      datesWithEvents.push(ds.start);

      if (isSameDay(ds.start, ds.end)) {
        continue;
      }

      let diffDays = this.calendarService.dateDiffInDays(ds.start, ds.end);
      do {
        const date = new Date(ds.end);
        date.setDate(date.getDate() - diffDays + 1);
        datesWithEvents.push(date);
        diffDays--;
      } while (diffDays > 0);
    }

    this.datesWithEvents = datesWithEvents;
  }

  private selectDay(direction: 1 | -1): void {
    const previous = new Date(this.selectedCalendarDate);
    const weekendEdge = direction < 0 ? CalendarDay.Monday : CalendarDay.Friday;
    if (this.hideWeekends && previous.getDay() === weekendEdge) {
      previous.setDate(previous.getDate() + 3 * direction);
    } else {
      previous.setDate(previous.getDate() + 1 * direction);
    }
    this.selectDate(previous);
  }

  private selectWeek(direction: 1 | -1): void {
    const next = new Date(this.selectedCalendarDate);
    next.setDate(next.getDate() + 7 * direction);
    this.selectDate(next);
  }

  private selectMonth(direction: 1 | -1): void {
    const next = new Date(this.selectedCalendarDate);
    next.setMonth(next.getMonth() + direction);
    next.setDate(1);
    if (this.hideWeekends && next.getDay() === CalendarDay.Sunday) {
      next.setDate(next.getDate() + 1);
    } else if (this.hideWeekends && next.getDay() === CalendarDay.Saturday) {
      next.setDate(next.getDate() + 2);
    }
    this.selectDate(next);
  }

  private selectPeriod(direction: 1 | -1): void {
    switch (this.calendarViewMode()) {
      case CalendarView.Day:
        this.selectDay(direction);
        break;
      case CalendarView.ThreeDay:
        this.selectDay(direction);
        this.selectDay(direction);
        this.selectDay(direction);
        break;
      case CalendarView.Week:
        this.selectWeek(direction);
        break;
      case CalendarView.Month:
        this.selectMonth(direction);
        break;
    }
  }

  private createWeekViewDateRange(): void {
    const startingDayIsWeekend = [CalendarDay.Saturday, CalendarDay.Sunday].includes(
      this.firstDayOfWeek,
    );
    const startWeekOn =
      startingDayIsWeekend && this.hideWeekends ? CalendarDay.Monday : this.firstDayOfWeek;

    this.weekViewDateRange = this.calendarService.createWeekDateRange(
      this.selectedCalendarDate,
      startWeekOn,
      this.hideWeekends,
      this.calendarViewMode(),
    );
  }

  private updateStyles(): void {
    const { general, dateSelector, appointments, container } = this.props?.style || {};
    const {
      default: dateSelectorDefault,
      hover: dateSelectorHover,
      selected: dateSelectorSelected,
    } = dateSelector || {};

    if (
      !general ||
      !appointments ||
      !dateSelector ||
      !container ||
      !dateSelectorDefault ||
      !dateSelectorHover ||
      !dateSelectorSelected
    ) {
      return;
    }

    this.titlesColor = general.titles.color1;
    this.gridBackgroundColor = general.gridBackground.color1;
    this.headerBackgroundColor = general.headerBackground.color1;
    this.linesColor = general.lines.selectorType === 'none' ? 'transparent' : general.lines.color1;
    this.secondaryLinesColor =
      general.secondaryLines.selectorType === 'none'
        ? 'transparent'
        : general.secondaryLines.color1;
    this.calendarOutlineColor =
      appointments.outline.selectorType === 'none' ? 'transparent' : appointments.outline.color1;

    this.dateSelectorDefaultText = dateSelectorDefault.day.color1;
    this.dateSelectorDefaultBackground = dateSelectorDefault.fill.color1;
    this.dateSelectorDefaultBorderColor =
      dateSelectorDefault.outline.selectorType === 'none'
        ? 'transparent'
        : dateSelectorDefault.outline.color1;
    this.dateSelectorDefaultBorderWidth = dateSelectorDefault.outlineWidth;
    this.dateSelectorDefaultBorderRadius = dateSelectorDefault.radius;
    this.dateSelectorHoverText = dateSelectorHover.day.color1;
    this.dateSelectorHoverBackground = dateSelectorHover.fill.color1;
    this.dateSelectorHoverBorderColor =
      dateSelectorHover.outline.selectorType === 'none'
        ? 'transparent'
        : dateSelectorHover.outline.color1;
    this.dateSelectorHoverBorderWidth = dateSelectorHover.outlineWidth;
    this.dateSelectorHoverBorderRadius = dateSelectorHover.radius;
    this.dateSelectorSelectedText = dateSelectorSelected.day.color1;
    this.dateSelectorSelectedBackground = dateSelectorSelected.fill.color1;
    this.dateSelectorSelectedBorderColor =
      dateSelectorSelected.outline.selectorType === 'none'
        ? 'transparent'
        : dateSelectorSelected.outline.color1;
    this.dateSelectorSelectedBorderWidth = dateSelectorSelected.outlineWidth;
    this.dateSelectorSelectedBorderRadius = dateSelectorSelected.radius;
    this.dateSelectorDayWithEvents = dateSelector.dayWithEvents.color1;

    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(general.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };

    this.internalPadding = general.offset.padding;

    setTimeout(() => {
      const componentStyles: Record<string, string> = {};
      for (let i = 0; i < this.elementRef.nativeElement.style.length; i++) {
        const property = this.elementRef.nativeElement.style[i];
        if (property.startsWith('--b-cr')) {
          componentStyles[property] =
            this.elementRef.nativeElement.style.getPropertyValue(property);
        }
      }
      this.componentStyles = componentStyles;
    });
  }

  private getActionData():
    | {
        dataSource: DataBindingSourceInfo;
        dataRowIdField: string;
        formContext: FormContextCalendar;
      }
    | undefined {
    if (!arePropsWithData(this.props) || !this.props?.content.input) {
      return;
    }
    const dataSource = this.props.data?.find((data) => data?.key === 'value')?.dataSource;

    if (!dataSource) {
      return;
    }

    const dataRowIdField = this.getDataRowIdField();

    const columns = this.getDatasetRowFieldKeys(this.props.content.input);
    const formContext: FormContextCalendar = {
      type: 'calendar',
      startColumn: columns.start,
      endColumn: columns.end,
      calendarColumn: columns.calendar,
      timeZoneColumn: columns.timeZone,
      defaultDurationMinutes: this.props.content.calendarOptions?.defaultDurationMinutes,
    };

    return { dataSource, dataRowIdField, formContext };
  }

  private updateEvent(dataRow: DatasetRow): void {
    const actionData = this.getActionData();
    if (!actionData) {
      return;
    }
    const { dataSource, dataRowIdField } = actionData;
    const datasetId = this.schemaService.getDatasetId(dataSource.id, dataSource.sourceType);
    if (!datasetId) {
      throw new Error('event dataset not found');
    }

    this.containerActionsService
      .handleRowUpdate(datasetId, dataRow[dataRowIdField], dataRow)
      .subscribe();
  }

  private updateQueryOutput(): void {
    const { startColumn, endColumn } = this.props?.content.input ?? {};
    if (!startColumn || !endColumn) {
      return;
    }

    const dataSource = getActiveBinding(this.props?.data, 'value')?.dataSource;
    if (!dataSource) {
      return;
    }

    let since = this.selectedCalendarDate;
    let until = this.selectedCalendarDate;

    if (this.calendarViewMode() === CalendarView.Month || this.isCalendarVisible) {
      since = addDays(startOfMonth(since), -7);
      until = addDays(endOfMonth(until), 7);
    } else if (this.calendarViewMode() === CalendarView.ThreeDay) {
      since = addDays(since, -1);
      until = addDays(until, 1);
    } else if (this.calendarViewMode() === CalendarView.Week) {
      since = startOfWeek(since);
      until = endOfWeek(until);
    }

    if (
      (isAfter(since, this.queryRange.since) || isSameDay(since, this.queryRange.since)) &&
      (isBefore(until, this.queryRange.until) || isSameDay(until, this.queryRange.until))
    ) {
      return; // the data needed is already fetched
    }

    this.queryRange = { since, until };

    const data: RuntimeEventQueryData = {
      page: { size: 0, index: 0, direction: PaginationDirection.$Initial },
      elementId: this.elementId as string,
      dataSource,
      query: getCalendarRangeQuery(startColumn, endColumn, { since, until }),
    };

    if (this.queryEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'query',
        data,
        isInitialData: false,
        output: 'pagination',
      });
    }
  }

  private updateDefaultEventSchema(): void {
    if (!this.bindingResults?.value) {
      this.setDefaultEventSchema('action');
    }

    this.getDataInput('value')
      .schema$.pipe(
        filter(Boolean),
        distinctUntilChanged(isDeepEqual),
        map((schema) => convertBlazeSchemaToElementSchema(schema, this.elementId, 'action')),
        untilDestroyed(this),
      )
      .subscribe((schema) => {
        this.setDefaultEventSchema('action', schema);
      });
  }

  private initDayViewColumnWidthUpdate(): void {
    const dayViewOptions$ = this.props$.pipe(
      map((props) => props?.content?.dayViewOptions),
      distinctUntilChanged(isDeepEqual),
    );

    const containerWidth$ = resizeRxUtils
      .fromResizeObserver(this.calendarWorkspace.nativeElement)
      .pipe(
        map((resizeEntries) => {
          const resizeData = resizeEntries[resizeEntries.length - 1];
          if (!resizeData) {
            return this.calendarWorkspace.nativeElement.getBoundingClientRect().width;
          }

          return resizeData.contentRect.width;
        }),
        countedShareReplay(),
      );

    const isSwipingMode$ = containerWidth$.pipe(
      map((containerWidth) => containerWidth > 0 && containerWidth < SWIPING_MODE_THRESHOLD_PX),
    );

    isSwipingMode$.pipe(untilDestroyed(this)).subscribe((isSwipingMode) => {
      this.isSwipingMode.set(isSwipingMode);
    });

    combineLatest({
      calendarViewMode: this.calendarViewMode$,
      dayViewOptions: dayViewOptions$,
      calendarsCount: this.calendarsCount$,
      containerWidth: containerWidth$,
      isSwipingMode: isSwipingMode$,
    })
      .pipe(
        untilDestroyed(this),
        map(
          ({ calendarViewMode, dayViewOptions, calendarsCount, containerWidth, isSwipingMode }) => {
            return this.calculateDayViewColumnWidth(
              calendarViewMode,
              dayViewOptions,
              calendarsCount,
              containerWidth,
              isSwipingMode,
            );
          },
        ),
      )
      .subscribe((width) => {
        this.dayViewColumnWidth.set(width);
      });
  }

  private calculateDayViewColumnWidth(
    calendarViewMode: CalendarView,
    dayViewOptions: CalendarDayViewOptions | undefined,
    calendarsCount: number,
    containerWidth: number,
    isSwipingMode: boolean,
  ): ValueWithUnitOfMeasure | undefined {
    if (containerWidth <= 0 && !this.calendarWorkspace) {
      return;
    }

    const { defaultDayViewColumnWidthPx, minDayViewColumnWidthPx } = this.config;

    /**
     * Width of scrollable calendar grid
     */
    const calendarGridWidth = containerWidth - this.timeLineWidthPx;

    if (calendarViewMode !== CalendarView.Day) {
      return undefined;
    }

    /**
     * When in swiping mode (mobile view) day is stretched to full width
     */
    if (isSwipingMode) {
      return `${calendarGridWidth}px`;
    }

    /**
     * Backwards compatibility
     */
    if (!dayViewOptions) {
      return `${defaultDayViewColumnWidthPx}px`;
    }

    let width = 0;
    const { widthValue, widthMode, showCalendarsNumber } = dayViewOptions;

    /**
     * Fit - Try to squeeze all calendars into view
     */
    if (widthMode === 'fit' && calendarsCount) {
      width = Math.max(minDayViewColumnWidthPx, calendarGridWidth / calendarsCount);

      /**
       * Count - Try to fit a specific number of calendars into view
       */
    } else if (widthMode === 'count' && calendarsCount) {
      // Added 4 pixels to hide border of possible next calendar
      const borderOffset = calendarsCount > showCalendarsNumber ? 4 : 0;
      width = Math.max(
        minDayViewColumnWidthPx,
        (calendarGridWidth - borderOffset) / showCalendarsNumber,
      );

      /**
       * (Default) Fixed - Set fixed size of calendars. This is used for backwards compatibility
       */
    } else {
      const widthIntValue = toInt(widthValue, defaultDayViewColumnWidthPx);
      width = Math.max(minDayViewColumnWidthPx, widthIntValue);
    }

    return `${Math.floor(width)}px`;
  }
}
